/**
 * Touch input — the phone half of the control scheme.
 *
 * A pogo climb needs three axes at once: lean, look and charge. On a desktop
 * that is WASD, the mouse and Space; on a phone there are two thumbs and no
 * pointer lock, so the screen itself has to be divided up:
 *
 * - **A floating stick, not a fixed one.** The stick anchors wherever the left
 *   thumb lands. A fixed pad demands the player look down to find it, and on a
 *   game where a mistimed lean costs forty metres, looking away from the
 *   mountain is the expensive part. Displacement is analog — a 10 px drag leans
 *   a tenth — which is what replaces Shift-precision on mobile.
 * - **The rest of the playfield is the camera.** Drag anywhere else to orbit,
 *   two fingers to pinch the distance. Pinching must not also spin the view, so
 *   while two look-zone fingers are down the look accumulator is deliberately
 *   frozen.
 * - **Charge is a held button, and letting go must always work.** A stuck
 *   `charging` flag is unrecoverable without a reload — the pogo would launch at
 *   full power on every landing forever — so the release path is covered three
 *   times over: `pointerup`, `pointercancel` and `pointerleave`, plus the blur /
 *   visibility sweep. Cheap insurance against an OS gesture stealing the finger.
 *
 * Everything is Pointer Events keyed by `pointerId`, with the moves and releases
 * bound to `window`: touch pointers get implicit capture on whatever received
 * the `pointerdown`, so element-level move listeners would fire for a finger
 * that has long since slid off the element it started on. Window-level plus an
 * id map is both simpler and impossible to lose a finger with.
 *
 * Mouse pointers are ignored throughout: on a hybrid device the `Input` class is
 * still live and would otherwise get the same drag twice.
 */

import '../ui/ui.css';
import type { FrameInput } from '../physics/pogo';
import type { MouseDelta } from './input';

/** Stick throw in CSS pixels — full lean at the rim. */
const JOY_RADIUS = 56;
/** Must match `.sk-touch__base` / `.sk-touch__knob` in ui.css: JS centres them. */
const JOY_BASE_PX = 112;
const JOY_KNOB_PX = 44;

/**
 * Wheel `deltaY` units per CSS pixel of pinch. One notch of a real wheel is ~100
 * and `camera.ts` turns that into 0.6 m, so 3 makes a 40 px pinch worth about a
 * metre of dolly — enough to feel, small enough to place.
 */
const PINCH_TO_WHEEL = 3;

/**
 * True for phones and tablets: a coarse *primary* pointer plus actual touch
 * hardware. Deliberately false for touchscreen laptops — their primary pointer
 * is the trackpad, they have a keyboard, and an on-screen stick over the view
 * would be a downgrade.
 */
export function isTouchDevice(): boolean {
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  return coarse && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
}

/** What a live finger is doing. The stick is exclusive; look fingers are a pair. */
interface TrackedTouch {
  mode: 'joy' | 'look';
  /** Last client position, so the next move can be differenced against it. */
  x: number;
  y: number;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  parent?.appendChild(node);
  return node;
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly base: HTMLElement;
  private readonly knob: HTMLElement;
  private readonly joyZone: HTMLElement;
  private readonly lookZone: HTMLElement;
  private readonly chargeBtn: HTMLButtonElement;
  private readonly pauseBtn: HTMLButtonElement;

  /** Live fingers by `pointerId`. The charge and pause buttons are not in here. */
  private readonly touches = new Map<number, TrackedTouch>();
  /** Look fingers in arrival order; the first two are the pinch pair. */
  private readonly lookIds: number[] = [];
  private readonly pauseHandlers: Array<() => void> = [];
  private readonly chargeCancelHandlers: Array<() => void> = [];

  private joyId: number | null = null;
  private joyAnchorX = 0;
  private joyAnchorY = 0;
  private leanX = 0;
  private leanZ = 0;

  private chargeId: number | null = null;
  private charging = false;

  private lookDX = 0;
  private lookDY = 0;
  private zoomAccum = 0;
  /** Last pinch span in px, or `-1` when there is no pair to measure. */
  private pinchDist = -1;

  private readonly frameInput: FrameInput = { leanX: 0, leanZ: 0, charging: false };
  private readonly delta: MouseDelta = { dx: 0, dy: 0 };
  private disposed = false;

  constructor() {
    const mount = document.getElementById('ui-root') ?? document.body;

    this.root = el('div', 'sk-touch', mount);
    this.root.hidden = true;

    // Painted back to front: the look zone underneath everything, the stick zone
    // over its corner of it, the buttons over both.
    this.lookZone = el('div', 'sk-touch__zone sk-touch__zone--look', this.root);
    this.joyZone = el('div', 'sk-touch__zone sk-touch__zone--joy', this.root);

    this.base = el('div', 'sk-touch__base', this.root);
    this.knob = el('div', 'sk-touch__knob', this.root);
    this.base.hidden = true;
    this.knob.hidden = true;

    this.chargeBtn = el('button', 'sk-touch__charge', this.root);
    this.chargeBtn.type = 'button';
    this.chargeBtn.textContent = 'JUMP';

    this.pauseBtn = el('button', 'sk-touch__pause', this.root);
    this.pauseBtn.type = 'button';
    this.pauseBtn.textContent = 'II';
    this.pauseBtn.title = 'Pause';

    this.joyZone.addEventListener('pointerdown', this.onJoyDown);
    this.lookZone.addEventListener('pointerdown', this.onLookDown);

    this.chargeBtn.addEventListener('pointerdown', this.onChargeDown);
    this.chargeBtn.addEventListener('pointerup', this.onChargeUp);
    this.chargeBtn.addEventListener('pointercancel', this.onChargeUp);
    this.chargeBtn.addEventListener('pointerleave', this.onChargeUp);

    this.pauseBtn.addEventListener('click', this.onPauseClick);

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  // --- continuous ---------------------------------------------------------

  /**
   * Player intent for this frame.
   *
   * @returns a reused object — read it, do not retain it.
   */
  frame(): FrameInput {
    const f = this.frameInput;
    f.leanX = this.leanX;
    f.leanZ = this.leanZ;
    f.charging = this.charging;
    return f;
  }

  /** Look-drag since the last call, in raw pixels. Zeroes the accumulator. */
  consumeLookDelta(): MouseDelta {
    this.delta.dx = this.lookDX;
    this.delta.dy = this.lookDY;
    this.lookDX = 0;
    this.lookDY = 0;
    return this.delta;
  }

  /** Pinch since the last call, in wheel `deltaY` units. Zeroes the accumulator. */
  consumeZoomDelta(): number {
    const z = this.zoomAccum;
    this.zoomAccum = 0;
    return z;
  }

  // --- discrete -----------------------------------------------------------

  /** Fire `cb` when the on-screen pause button is tapped. */
  onPause(cb: () => void): void {
    this.pauseHandlers.push(cb);
  }

  /**
   * Fire `cb` when a held charge is taken away rather than let go — a
   * `pointercancel`, a blur, the tab going hidden. Under release-to-jump
   * physics the caller must tell those apart: a cancel swallowed as a release
   * would fire a full-power jump the player never asked for.
   */
  onChargeCancel(cb: () => void): void {
    this.chargeCancelHandlers.push(cb);
  }

  // --- visibility ---------------------------------------------------------

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
    // Hiding mid-hold would otherwise strand the lean and the charge.
    this.clearAll();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.pauseHandlers.length = 0;
    this.clearAll();
    this.chargeCancelHandlers.length = 0;
    this.root.remove();
  }

  // --- stick --------------------------------------------------------------

  /** Plant the base ring where the thumb landed and zero the knob. */
  private anchorJoystick(x: number, y: number): void {
    this.joyAnchorX = x;
    this.joyAnchorY = y;
    this.base.style.transform = `translate3d(${x - JOY_BASE_PX / 2}px, ${y - JOY_BASE_PX / 2}px, 0)`;
    this.base.hidden = false;
    this.knob.hidden = false;
    this.moveJoystick(x, y);
  }

  /** Track the thumb, clamped to the rim, and derive the lean from the offset. */
  private moveJoystick(x: number, y: number): void {
    let dx = x - this.joyAnchorX;
    let dy = y - this.joyAnchorY;
    const m = Math.hypot(dx, dy);
    if (m > JOY_RADIUS) {
      const s = JOY_RADIUS / m;
      dx *= s;
      dy *= s;
    }
    this.leanX = dx / JOY_RADIUS;
    // Screen Y grows downward; dragging up is leaning away from the camera.
    this.leanZ = -dy / JOY_RADIUS;
    const kx = this.joyAnchorX + dx - JOY_KNOB_PX / 2;
    const ky = this.joyAnchorY + dy - JOY_KNOB_PX / 2;
    this.knob.style.transform = `translate3d(${kx}px, ${ky}px, 0)`;
  }

  private releaseJoystick(): void {
    this.joyId = null;
    this.leanX = 0;
    this.leanZ = 0;
    this.base.hidden = true;
    this.knob.hidden = true;
  }

  // --- pinch --------------------------------------------------------------

  /** Distance between the pinch pair, or `-1` when there is not one. */
  private pinchSpan(): number {
    const a = this.lookIds[0];
    const b = this.lookIds[1];
    if (a === undefined || b === undefined) return -1;
    const ta = this.touches.get(a);
    const tb = this.touches.get(b);
    if (ta === undefined || tb === undefined) return -1;
    return Math.hypot(ta.x - tb.x, ta.y - tb.y);
  }

  // --- bookkeeping --------------------------------------------------------

  private endTouch(id: number): void {
    const t = this.touches.get(id);
    if (t === undefined) return;
    this.touches.delete(id);
    if (t.mode === 'joy') {
      if (this.joyId === id) this.releaseJoystick();
      return;
    }
    const i = this.lookIds.indexOf(id);
    if (i >= 0) this.lookIds.splice(i, 1);
    // Re-seed rather than keep the old span: lifting one finger of a pinch would
    // otherwise read as one enormous zoom on the next move.
    this.pinchDist = this.pinchSpan();
  }

  private setCharging(on: boolean): void {
    if (this.charging === on) return;
    this.charging = on;
    this.chargeBtn.classList.toggle('is-held', on);
  }

  /** Drop every finger. Same reasoning as `Input.onBlur`: nothing may stick. */
  private clearAll(): void {
    this.touches.clear();
    this.lookIds.length = 0;
    this.pinchDist = -1;
    this.lookDX = 0;
    this.lookDY = 0;
    this.zoomAccum = 0;
    this.chargeId = null;
    this.releaseJoystick();
    // This path is a cancellation, not a release: the finger was taken, not
    // lifted. Whoever holds the charge needs to know the difference.
    if (this.charging) for (const cb of this.chargeCancelHandlers.slice()) cb();
    this.setCharging(false);
  }

  // --- listeners ----------------------------------------------------------

  private readonly onJoyDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return;
    e.preventDefault();
    // One stick. A second thumb in the same corner is a misgrip, not an input.
    if (this.joyId !== null) return;
    this.joyId = e.pointerId;
    this.touches.set(e.pointerId, { mode: 'joy', x: e.clientX, y: e.clientY });
    this.anchorJoystick(e.clientX, e.clientY);
  };

  private readonly onLookDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return;
    e.preventDefault();
    if (this.touches.has(e.pointerId)) return;
    this.touches.set(e.pointerId, { mode: 'look', x: e.clientX, y: e.clientY });
    this.lookIds.push(e.pointerId);
    // Seed the span on the second finger so the first move reports the *change*
    // and not the whole gap between the fingers.
    this.pinchDist = this.pinchSpan();
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    const t = this.touches.get(e.pointerId);
    if (t === undefined) return;
    const dx = e.clientX - t.x;
    const dy = e.clientY - t.y;
    t.x = e.clientX;
    t.y = e.clientY;

    if (t.mode === 'joy') {
      this.moveJoystick(e.clientX, e.clientY);
      return;
    }

    // Two fingers on the look zone is a pinch, and a pinch that also spun the
    // camera would be unusable — the view is frozen for the duration.
    if (this.lookIds.length >= 2) {
      const span = this.pinchSpan();
      if (span >= 0) {
        if (this.pinchDist >= 0) this.zoomAccum -= (span - this.pinchDist) * PINCH_TO_WHEEL;
        this.pinchDist = span;
      }
      return;
    }

    this.lookDX += dx;
    this.lookDY += dy;
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    this.endTouch(e.pointerId);
    if (this.chargeId === e.pointerId) {
      this.chargeId = null;
      this.setCharging(false);
    }
  };

  /**
   * A cancel is the browser taking the finger away mid-gesture — an edge swipe,
   * a notification shade, a phone call. There is no release to wait for, so drop
   * everything rather than trust an id we may never hear about again.
   */
  private readonly onPointerCancel = (): void => {
    this.clearAll();
  };

  private readonly onChargeDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return;
    e.preventDefault();
    // One button, one finger: a second finger overwriting the id would let the
    // first finger's release drop the charge out from under the one still held.
    if (this.chargeId !== null) return;
    this.chargeId = e.pointerId;
    this.setCharging(true);
  };

  private readonly onChargeUp = (): void => {
    this.chargeId = null;
    this.setCharging(false);
  };

  private readonly onPauseClick = (): void => {
    for (const cb of this.pauseHandlers.slice()) cb();
  };

  private readonly onBlur = (): void => {
    this.clearAll();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.clearAll();
  };
}
